import React, { ComponentProps, ReactElement } from 'react';

import PropTypes from 'prop-types';

import {
  getValidChildren,
  getValidChildrenCount,
} from '@adsk/alloy-react-helpers';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import AccordionItem from './AccordionItem';
import useAccordion from './hooks/useAccordion';

type ChildAccordionType = ReactElement<ComponentProps<typeof AccordionItem>>;

const defaultRenderItem = (itemProps: ComponentProps<typeof AccordionItem>) => (
  <AccordionItem {...itemProps} />
);

type AccordionProps = StylableComponent<HTMLDivElement> & {
  initialExpanded?: number[];
  multiExpand?: boolean;
  renderItem?: (props: ComponentProps<typeof AccordionItem>) => ReactElement;
};
/**
 * An accordion component.
 */
const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      style,
      className,
      children,
      initialExpanded,
      multiExpand,
      renderItem = defaultRenderItem,
      ...props
    },
    ref,
  ) => {
    const { getItemProps } = useAccordion({
      initialExpanded,
      multiExpand,
      itemCount: getValidChildrenCount(children),
    });

    return (
      <div
        className={className}
        css={[
          {
            width: '100%',
          },
          style,
        ]}
        ref={ref}
        {...props}
      >
        {React.Children.map<ReactElement, ChildAccordionType>(
          getValidChildren(children),
          (child, index) =>
            renderItem({
              ...getItemProps({ index }),
              ...child.props,
            }),
        )}
      </div>
    );
  },
);

Accordion.displayName = 'Accordion';

Accordion.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The accordion items that make up this Accordion */
  children: PropTypes.any,
  /** An array of item indexes to expand initially */
  initialExpanded: PropTypes.arrayOf(PropTypes.number.isRequired),
  /** Whether or not multiple items can be expanded at once */
  multiExpand: PropTypes.bool,
  /** Render custom accordian item */
  renderItem: PropTypes.func,
};

export default Accordion;
